import { useRoute, Link, Redirect } from 'wouter';
import { useTranslation } from 'react-i18next';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import { localizeHref } from '@/lib/marketing/localizedLandings';
import remarkGfm from 'remark-gfm';
import { Calendar, Clock, ArrowLeft } from 'lucide-react';
import { LandingLayout } from '@/pages/landing/shared/LandingLayout';
import { SEOHead } from '@/pages/landing/shared/SEOHead';
import {
  generateArticleSchema,
  generateBreadcrumbSchema,
  generateCanonicalUrl,
  combineSchemas,
} from '@/utils/seo';
import { getBlogPost } from '@/content/blog/loader';

export default function BlogPostPage() {
  const { t, i18n } = useTranslation(['blog', 'common']);
  const [, params] = useRoute<{ slug: string }>('/blog/:slug');
  const slug = params?.slug;

  if (!slug) return <Redirect to="/blog" />;

  const post = getBlogPost(slug, i18n.language);

  if (!post) return <Redirect to="/blog" />;

  const canonicalUrl = generateCanonicalUrl(`/blog/${slug}`);

  const structuredData = combineSchemas(
    generateArticleSchema({
      headline: post.meta.title,
      description: post.meta.description,
      url: canonicalUrl,
      datePublished: post.meta.date,
      author: post.meta.author,
    }),
    generateBreadcrumbSchema([
      { name: 'Home', url: 'https://slotyapp.com' },
      { name: t('blog:title'), url: 'https://slotyapp.com/blog' },
      { name: post.meta.title, url: canonicalUrl },
    ])
  );

  const dateLocale = i18n.language === 'de' ? 'de-DE' : i18n.language === 'pt-BR' ? 'pt-BR' : i18n.language === 'pt-PT' ? 'pt-PT' : 'en-US';

  return (
    <LandingLayout>
      <SEOHead
        title={post.meta.title}
        description={post.meta.description}
        keywords={post.meta.keywords}
        canonical={canonicalUrl}
        ogType="article"
        structuredData={structuredData}
      />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back link */}
        <Link
          href="/blog"
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-brand-600 mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('blog:backToBlog')}
        </Link>

        {/* Header */}
        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 font-display leading-tight">
            {post.meta.title}
          </h1>
          <div className="mt-4 flex items-center gap-4 text-sm text-slate-500">
            <span>{post.meta.author}</span>
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {new Date(post.meta.date).toLocaleDateString(dateLocale, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
            {post.meta.readingTime && (
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {t('blog:readingTime', { minutes: post.meta.readingTime })}
              </span>
            )}
          </div>
        </header>

        {/* Content */}
        <div className="prose prose-slate prose-lg max-w-none prose-headings:font-display prose-a:text-brand-600 hover:prose-a:text-brand-700">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            // Links to landing pages go to the version in the post's language (/de/fuer-friseursalons).
            urlTransform={(url) => localizeHref(defaultUrlTransform(url), i18n.language)}
          >
            {post.content}
          </ReactMarkdown>
        </div>

        {/* CTA */}
        <div className="mt-12 p-8 bg-brand-50 rounded-xl text-center">
          <h2 className="text-xl font-semibold text-slate-900">
            {t('blog:cta.title')}
          </h2>
          <p className="mt-2 text-slate-600">
            {t('blog:cta.description')}
          </p>
          <Link
            href="/register"
            className="mt-4 inline-block bg-brand-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-brand-700 transition-colors"
          >
            {t('blog:cta.button')}
          </Link>
        </div>
      </article>
    </LandingLayout>
  );
}
